import React, { useState } from 'react';

import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import ChevronDown from '@atlaskit/icon/glyph/chevron-down';
import ChevronRight from '@atlaskit/icon/glyph/chevron-right';
import FolderIcon from '@atlaskit/icon/glyph/folder';
import FolderFilledIcon from '@atlaskit/icon/glyph/folder-filled';

import { Folder as FolderModel } from '@qality/common-model';
import { useTestCaseRepositoryIssuesContext } from '@qality/common-pages/src/TestCaseRepositoryPage';
import { useTestCaseRepositoryFoldersContext } from '@qality/common-pages/src/TestCaseRepositoryPage/TestCaseRepositoryFoldersProvider';
import { TestCaseRepositoryTable } from '@qality/common-pages/src/TestCaseRepositoryPage/TestCaseRepositoryTable';

import { EllipseStyled } from '../../atoms/Ellipse';
import EditableFolderName from '../EditableFolderName/EditableFolderName';
import {
  BodyStyled,
  ChevronButtonStyled,
  ContainerStyled,
  ContentTextStyled,
  FolderIconStyled,
  FooterStyled,
  HeaderStyled,
  NameStyled,
} from './SubfolderBox.styled';

type Props = { folder: FolderModel };

export function SubfolderBox({ folder }: Props): JSX.Element {
  const { initSubfolder, issues, loading } =
    useTestCaseRepositoryIssuesContext();

  const { openFolder, expandFolder } = useTestCaseRepositoryFoldersContext();

  const handleClick = async () => {
    await openFolder(folder);
    await expandFolder(folder);
  };

  const [isExpanded, setIsExpanded] = useState<boolean | null>(null);

  const filteredIssuesLabel =
    issues.length !== folder.totalTestCases && isExpanded
      ? `${issues.length}/${folder.totalTestCases}`
      : `${folder.totalTestCases}`;

  const totalTestCasesLabel =
    folder.totalTestCases === 1
      ? `${filteredIssuesLabel} test case`
      : `${filteredIssuesLabel} test cases`;

  const totalSubfoldersLabel =
    folder.totalSubfolders === 1
      ? '1 folder'
      : `${folder.totalSubfolders} folders`;

  const isFolderEmpty =
    folder.totalSubfolders === 0 && folder.totalTestCases === 0;

  const handleToggleExpandSubfolderPreview = async () => {
    if (isExpanded === null) {
      await initSubfolder();
      setIsExpanded(true);
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;
  const buttonLabel = isExpanded
    ? 'Collapse subfolder preview'
    : 'Expand subfolder preview';

  return (
    <ContainerStyled>
      <HeaderStyled>
        <FolderIconStyled>
          {isFolderEmpty ? (
            <FolderIcon label='folder icon' />
          ) : (
            <FolderFilledIcon label='filled folder icon' />
          )}
        </FolderIconStyled>
        <NameStyled>
          <EditableFolderName folder={folder} />
        </NameStyled>
        <ContentTextStyled>
          {totalTestCasesLabel}
          <EllipseStyled subtler />
          {totalSubfoldersLabel}
        </ContentTextStyled>
        <ChevronButtonStyled>
          <IconButton
            appearance='subtle'
            icon={ChevronIcon}
            label={buttonLabel}
            onClick={handleToggleExpandSubfolderPreview}
            isLoading={loading !== 'none'}
          />
        </ChevronButtonStyled>
      </HeaderStyled>
      {isExpanded && (
        <>
          <BodyStyled>
            <TestCaseRepositoryTable numberOfSkeletonRows={3} folder={folder} />
          </BodyStyled>
          <FooterStyled>
            <Button
              iconAfter={<ArrowRightIcon label='arrow icon' />}
              appearance='subtle'
              onClick={handleClick}
              style={{ marginRight: '10px' }}
            >
              Open folder
            </Button>
          </FooterStyled>
        </>
      )}
    </ContainerStyled>
  );
}
